"use client";

import { ChartConfig } from "@/components/ui/Chart";
import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { useWatchDogContext } from "@/context/WatchDogContext";
import { isDateInWatchDogRange } from "@/lib/date";
import { ChartDataItem, EstateOffer } from "@/types";
import { fetchChildrenResults } from "@/utils";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

function parseNumericField(
  value: string | number | null | undefined,
): number | undefined {
  const parsed =
    typeof value === "string" ? parseFloat(value.replace(/[^\d.]/g, "")) : value;
  return parsed !== undefined && parsed !== null && !isNaN(parsed)
    ? parsed
    : undefined;
}

type AnalysisContextType = {
  isGraphView: boolean;
  setIsGraphView: React.Dispatch<React.SetStateAction<boolean>>;
  chartData: { date: string; price: number; offer: EstateOffer }[];
  childrenEstateOffers: EstateOffer[];
  isLoadingAnalysis: boolean;
  activeChart: keyof typeof chartConfig;
  setActiveChart: React.Dispatch<
    React.SetStateAction<keyof typeof chartConfig>
  >;
  chartConfig: typeof chartConfig;
  total: {
    avgPrice: number;
    median: number;
    totalEstates: number;
    avgPricePerSquareMeter?: number;
    avgSquareMeter?: number;
  };
  analysis: {
    avgPriceBefore: number;
    avgPriceAfter: number;
    highestRent: number;
    avgSquareMeterBefore?: number;
    avgSquareMeterAfter?: number;
  };
  saleEstateOffers: EstateOffer[];
  sortedFilteredData: { date: string; price: number; offer: EstateOffer }[];
  handleMouseMove: (state: any) => void;
  handleMouseLeave: () => void;
  handleDataPointClick(point: ChartDataItem): void;
  clearSelectedDataPoint: () => void;
  pinnedDataPoint: ChartDataItem | null;
  hoveredDataPoint: ChartDataItem | null;
  childChartDataBeforeReco: {
    date: string;
    price: number;
    offer: EstateOffer;
  }[];
  childChartDataAfterReco: {
    date: string;
    price: number;
    offer: EstateOffer;
  }[];
};

const SOFTIQ_PRIMARY = "#1B4242";

const chartConfig = {
  views: {
    label: "Cena",
  },
  avgPrice: {
    label: "Průměrná cena",
    color: SOFTIQ_PRIMARY,
  },
  median: {
    label: "Medián",
  },
  totalEstates: {
    label: "Celkem nemovitostí",
  },
  avgPriceBefore: {
    label: "v aktuálním stavu",
    color: SOFTIQ_PRIMARY,
  },
  avgPriceAfter: {
    label: "po kompletní rekonstrukci",
    color: SOFTIQ_PRIMARY,
  },
} satisfies ChartConfig;

const defaultContext: AnalysisContextType = {
  isGraphView: false,
  setIsGraphView: () => {},
  chartData: [],
  childrenEstateOffers: [],
  isLoadingAnalysis: true,
  activeChart: "avgPrice",
  setActiveChart: () => {},
  chartConfig,
  total: {
    avgPrice: 0,
    median: 0,
    totalEstates: 0,
    avgPricePerSquareMeter: 0,
    avgSquareMeter: 0,
  },
  analysis: {
    avgPriceBefore: 0,
    avgPriceAfter: 0,
    highestRent: 0,
    avgSquareMeterBefore: 0,
    avgSquareMeterAfter: 0,
  },
  saleEstateOffers: [],
  sortedFilteredData: [],
  handleMouseMove: () => {},
  handleMouseLeave: () => {},
  handleDataPointClick: () => {},
  clearSelectedDataPoint: () => {},
  pinnedDataPoint: null,
  hoveredDataPoint: null,
  childChartDataBeforeReco: [],
  childChartDataAfterReco: [],
};

const AnalysisContext = createContext<AnalysisContextType>(defaultContext);

export const AnalysisProvider = ({ children }: { children: ReactNode }) => {
  const { language } = useLanguage();
  const copy = {
    cs: {
      price: "Cena",
      averagePrice: "Průměrná cena",
      median: "Medián",
      totalProperties: "Celkem nemovitostí",
      currentState: "v aktuálním stavu",
      renovated: "po kompletní rekonstrukci",
    },
    en: {
      price: "Price",
      averagePrice: "Average price",
      median: "Median",
      totalProperties: "Total properties",
      currentState: "in current condition",
      renovated: "after complete renovation",
    },
  }[language];
  const localizedChartConfig = React.useMemo(
    () =>
      ({
        views: {
          label: copy.price,
        },
        avgPrice: {
          label: copy.averagePrice,
          color: SOFTIQ_PRIMARY,
        },
        median: {
          label: copy.median,
        },
        totalEstates: {
          label: copy.totalProperties,
        },
        avgPriceBefore: {
          label: copy.currentState,
          color: SOFTIQ_PRIMARY,
        },
        avgPriceAfter: {
          label: copy.renovated,
          color: SOFTIQ_PRIMARY,
        },
      }) satisfies typeof chartConfig,
    [
      copy.averagePrice,
      copy.currentState,
      copy.median,
      copy.price,
      copy.renovated,
      copy.totalProperties,
    ],
  );
  const { sortedEstateOffers, dateRange } = useWatchDogContext();
  const [isGraphView, setIsGraphView] = useState(defaultContext.isGraphView);
  const [chartData, setChartData] = useState<
    { date: string; price: number; offer: EstateOffer }[]
  >([]);
  const [childrenEstateOffers, setChildrenEstateOffers] = useState<
    EstateOffer[]
  >([]);

  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(
    defaultContext.isLoadingAnalysis,
  );

  const { userInfoGlobal, setErrorMessage } = useAppContext();
  const { selectedDog } = useWatchDogContext();

  useEffect(() => {
    const newChartData: { date: string; price: number; offer: EstateOffer }[] =
      [];

    sortedEstateOffers.forEach((item) => {
      const numericPrice = item.price.toString().replace(/[^\d.]/g, "");
      const priceNumber = Number(numericPrice);

      if (!isNaN(priceNumber) && priceNumber > 0) {
        newChartData.push({
          date: item.createdAt,
          price: priceNumber,
          offer: item,
        });
      }
    });

    setChartData(newChartData);
  }, [sortedEstateOffers]);

  const [activeChart, setActiveChart] =
    React.useState<keyof typeof chartConfig>("avgPrice");

  const filteredData = chartData;

  const total = React.useMemo(() => {
    const count = filteredData.length;

    if (count === 0) {
      return {
        avgPrice: 0,
        median: 0,
        totalEstates: 0,
        avgPricePerSquareMeter: 0,
        avgSquareMeter: 0,
      };
    }

    const prices = filteredData.map((item) => item.price).sort((a, b) => a - b);

    const sum = filteredData.reduce((acc, curr) => acc + curr.price, 0);

    const avg = Math.round(sum / count);

    const middle = Math.floor(count / 2);
    const median =
      count % 2 === 0
        ? Math.round((prices[middle - 1] + prices[middle]) / 2)
        : prices[middle];

    // Calculate average price per square meter (from BE-provided pricePerSquareMeter) and average square meters
    const offersWithSquareMeters = filteredData.filter((item) => {
      const sqm = parseNumericField(item.offer.squareMeter);
      return sqm !== undefined && sqm > 0;
    });

    let avgPricePerSquareMeter: number | undefined;
    let avgSquareMeter: number | undefined;

    if (offersWithSquareMeters.length > 0) {
      const totalSquareMeters = offersWithSquareMeters.reduce((acc, curr) => {
        return acc + (parseNumericField(curr.offer.squareMeter) || 0);
      }, 0);
      avgSquareMeter = Math.round(
        totalSquareMeters / offersWithSquareMeters.length,
      );

      const offersWithPricePerSqm = filteredData.filter((item) => {
        const pricePerSqm = parseNumericField(item.offer.pricePerSquareMeter);
        return pricePerSqm !== undefined && pricePerSqm > 0;
      });

      if (offersWithPricePerSqm.length > 0) {
        const totalPricePerSqm = offersWithPricePerSqm.reduce((acc, curr) => {
          return acc + (parseNumericField(curr.offer.pricePerSquareMeter) || 0);
        }, 0);
        avgPricePerSquareMeter = Math.round(
          totalPricePerSqm / offersWithPricePerSqm.length,
        );
      }
    }

    return {
      avgPrice: avg,
      median,
      totalEstates: count,
      avgPricePerSquareMeter,
      avgSquareMeter,
    };
  }, [filteredData]);

  const filteredChildrenEstateOffers = React.useMemo(
    () =>
      childrenEstateOffers.filter((offer) =>
        isDateInWatchDogRange(offer.createdAt, dateRange),
      ),
    [childrenEstateOffers, dateRange],
  );

  const {
    analysis,
    childChartDataBeforeReco,
    childChartDataAfterReco,
    saleEstateOffers,
  } = React.useMemo(() => {
    // The rent children (before/after reko) come first, the sale child
    // (used only to derive price/m²) is identified by its offerType so
    // ordering of the BE response does not matter.
    const rentIds = Array.from(
      new Set(
        filteredChildrenEstateOffers
          .filter((i) => i.watchdogEntity.offerType !== "Prodej")
          .map((i) => i.watchdogEntity.id),
      ),
    );
    const saleIds = Array.from(
      new Set(
        filteredChildrenEstateOffers
          .filter((i) => i.watchdogEntity.offerType === "Prodej")
          .map((i) => i.watchdogEntity.id),
      ),
    );

    const saleOffers = filteredChildrenEstateOffers.filter((item) =>
      saleIds.includes(item.watchdogEntity.id),
    );

    if (rentIds.length < 2) {
      return {
        analysis: {
          avgPriceBefore: 0,
          avgPriceAfter: 0,
          highestRent: 0,
          avgSquareMeterBefore: undefined,
          avgSquareMeterAfter: undefined,
        },
        childChartDataBeforeReco: [],
        childChartDataAfterReco: [],
        saleEstateOffers: saleOffers,
      };
    }

    const [idA, idB] = rentIds;

    const groupA: { date: string; price: number; offer: EstateOffer }[] = [];
    const groupB: { date: string; price: number; offer: EstateOffer }[] = [];
    let highestValue = 0;

    filteredChildrenEstateOffers.forEach((item) => {
      const numericPrice = item.price.toString().replace(/[^\d.]/g, "");
      const priceNumber = Number(numericPrice);

      if (!isNaN(priceNumber) && priceNumber > 0) {
        const dataItem = {
          date: item.createdAt,
          price: priceNumber,
          offer: item,
        };

        if (item.watchdogEntity.id === idA) {
          groupA.push(dataItem);
        } else if (item.watchdogEntity.id === idB) {
          groupB.push(dataItem);
        }

        if (highestValue < priceNumber) {
          highestValue = priceNumber;
        }
      }
    });

    function median(values: number[]) {
      if (values.length === 0) return 0;
      const sorted = [...values].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      if (sorted.length % 2 !== 0) {
        return sorted[mid];
      } else {
        return Math.round((sorted[mid - 1] + sorted[mid]) / 2);
      }
    }

    const medianA = median(groupA.map((i) => i.price));
    const medianB = median(groupB.map((i) => i.price));

    function getSqm(item: { offer: EstateOffer }) {
      return parseNumericField(item.offer.squareMeter);
    }

    // Size (m²) of a listing is meaningful for both rent and sale offers, so
    // it keeps being derived from the rent (before/after reko) children.
    // Price/m² is derived separately from the sale child (saleEstateOffers)
    // since price/m² computed from rent listings isn't meaningful.
    const offersWithSqmBefore = groupA.filter((item) => {
      const sqm = getSqm(item);
      return sqm !== undefined && sqm > 0;
    });

    let avgSquareMeterBefore: number | undefined;
    if (offersWithSqmBefore.length > 0) {
      avgSquareMeterBefore = median(
        offersWithSqmBefore.map((item) => getSqm(item) || 0),
      );
    }

    const offersWithSqmAfter = groupB.filter((item) => {
      const sqm = getSqm(item);
      return sqm !== undefined && sqm > 0;
    });

    let avgSquareMeterAfter: number | undefined;
    if (offersWithSqmAfter.length > 0) {
      const medianSqmAfter = median(
        offersWithSqmAfter.map((item) => getSqm(item) || 0),
      );
      avgSquareMeterAfter =
        avgSquareMeterBefore !== undefined && medianSqmAfter <= avgSquareMeterBefore
          ? avgSquareMeterBefore
          : medianSqmAfter;
    }

    return {
      analysis: {
        avgPriceBefore: medianA,
        avgPriceAfter: medianB > medianA ? medianB : medianA,
        highestRent: highestValue,
        avgSquareMeterBefore,
        avgSquareMeterAfter,
      },
      childChartDataBeforeReco: groupA,
      childChartDataAfterReco: groupB,
      saleEstateOffers: saleOffers,
    };
  }, [filteredChildrenEstateOffers]);

  const [sortedFilteredData, setSortedFilteredData] = useState<
    { date: string; price: number; offer: EstateOffer }[]
  >([]);

  useEffect(() => {
    let dataToSet: { date: string; price: number; offer: EstateOffer }[] = [];

    switch (activeChart) {
      case "avgPriceBefore":
        dataToSet = childChartDataBeforeReco;
        break;
      case "avgPriceAfter":
        dataToSet = childChartDataAfterReco;
        break;
      case "avgPrice":
        dataToSet = filteredData;
        break;
      default:
        dataToSet = filteredData;
    }

    const sortedData = [...dataToSet].sort((a, b) => {
      const timeDiff = new Date(a.date).getTime() - new Date(b.date).getTime();

      if (timeDiff !== 0) {
        return timeDiff;
      }

      return a.offer.id - b.offer.id;
    });

    // Compare with current state before updating
    setSortedFilteredData((prev) => {
      const prevString = JSON.stringify(prev);
      const nextString = JSON.stringify(sortedData);
      return prevString !== nextString ? sortedData : prev;
    });
  }, [
    activeChart,
    childChartDataBeforeReco,
    childChartDataAfterReco,
    filteredData,
  ]);

  // State to hold last hovered data point; start with first data point or null if none
  const [hoveredDataPoint, setHoveredDataPoint] =
    React.useState<ChartDataItem | null>(
      filteredData.length > 0 ? filteredData[0] : null,
    );

  const handleMouseMove = (state: any) => {
    if (state && state.activePayload && state.activePayload.length) {
      const hoveredPoint = state.activePayload[0].payload as ChartDataItem;
      setHoveredDataPoint(hoveredPoint);
    }
  };

  const [pinnedDataPoint, setPinnedDataPoint] =
    React.useState<ChartDataItem | null>(null);

  const handleDataPointClick = (dataPoint: ChartDataItem) => {
    if (dataPoint == pinnedDataPoint) {
      setPinnedDataPoint(null);
    } else {
      setPinnedDataPoint(dataPoint);
    }
    setHoveredDataPoint(dataPoint);
  };

  const clearSelectedDataPoint = () => {
    setPinnedDataPoint(null);
    setHoveredDataPoint(null);
  };

  useEffect(() => {
    const visibleOfferIds = new Set(
      sortedFilteredData.map((item) => item.offer.id),
    );

    setPinnedDataPoint((currentPinned) =>
      currentPinned && visibleOfferIds.has(currentPinned.offer.id)
        ? currentPinned
        : null,
    );

    setHoveredDataPoint((currentHovered) =>
      currentHovered && visibleOfferIds.has(currentHovered.offer.id)
        ? currentHovered
        : null,
    );
  }, [sortedFilteredData]);

  const handleMouseLeave = () => {
    if (pinnedDataPoint) {
      setHoveredDataPoint(pinnedDataPoint);
    } else {
      setHoveredDataPoint(
        sortedFilteredData.length > 0
          ? sortedFilteredData[sortedFilteredData.length - 1]
          : null,
      );
    }
  };

  // CHILD DATA
  useEffect(() => {
    if (isGraphView) {
      setChildrenEstateOffers([]);
      getChildrenResults();
    }
  }, [isGraphView, selectedDog]);

  const { editSpecificResult } = useWatchDogContext();
  useEffect(() => {
    setHoveredDataPoint(null);
  }, [editSpecificResult]);

  const getChildrenResults = async () => {
    try {
      if (!userInfoGlobal?.token) return;
      setIsLoadingAnalysis(true);
      const payload = {
        email: userInfoGlobal.email,
        token: userInfoGlobal.token,
        data: {
          watchDogId: selectedDog?.id,
        },
      };
      const response = await fetchChildrenResults(
        payload,
        userInfoGlobal.token,
      );
      if (response.status === 200) {
        setChildrenEstateOffers(response.data.data);
      }
    } catch (err) {
      setErrorMessage(String(err));
    } finally {
      setIsLoadingAnalysis(false);
    }
  };

  return (
    <AnalysisContext.Provider
      value={{
        isGraphView,
        setIsGraphView,
        chartData,
        childrenEstateOffers,
        isLoadingAnalysis,
        activeChart,
        setActiveChart,
        chartConfig: localizedChartConfig,
        total,
        analysis,
        saleEstateOffers,
        sortedFilteredData,
        handleMouseMove,
        handleMouseLeave,
        handleDataPointClick,
        clearSelectedDataPoint,
        pinnedDataPoint,
        hoveredDataPoint,
        childChartDataBeforeReco,
        childChartDataAfterReco,
      }}
    >
      {children}
    </AnalysisContext.Provider>
  );
};

export const useAnalysisContext = () => useContext(AnalysisContext);
